import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { getUser } from "api/axios";
import messagesService from "api/messagesService";
import statusService from "api/statusService";
import CourtPortalService from "api/CourtPortalService";
import Avatar from "components/Avatar";
import PDFViewer from "components/PDFViewer";
import OutgoingParcelReceiverRow from "components/OutgoingParcelReceiverRow";
import PdfIcon from "assets/icons/pdf.svg?react";
import DownloadIcon from "assets/icons/download.svg?react";
import { Evidence, IMetric, Receiver } from "dto/IMetric";
import { EParcelType } from "dto/EParcelType";
import { EEvidenceEvent } from "dto/EEvidenceEvent";
import { ItemKind } from "dto/IKindItems";
import { ESendStatus } from "components/IconsState/typings";
import { getItemKindName, getParcelTypeName } from "utils/getItemKindName";
import { randomString } from "utils/randomString";
import { FC, Props } from "./typings";
import styles from "./styles.module.scss";

const fmtDateTime = (iso?: string): string =>
  iso ? moment(iso).format("YYYY.MM.DD HH:mm") : "—";

const downloadBlob = async (url: string, fileName: string, type?: string) => {
  const token = getUser()?.access_token;
  const res = await axios.get(url, {
    responseType: "blob",
    headers: { Authorization: `Bearer ${token}` },
  });
  const objectUrl = window.URL.createObjectURL(
    new Blob([res.data], type ? { type } : undefined),
  );
  const link = document.createElement("a");
  link.href = objectUrl;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const fetchBlob = async (url: string, type = "application/pdf"): Promise<Blob> => {
  const token = getUser()?.access_token;
  const res = await axios.get(url, {
    responseType: "blob",
    headers: { Authorization: `Bearer ${token}` },
  });
  return new Blob([res.data], { type });
};

const API = import.meta.env.VITE_APP_API_URL;

// Pojedynczy odbiorca w sekcji „Dowody doręczenia" — zwijana lista dowodów:
// ikona PDF otwiera podgląd w panelu slide, ikona pobierania zapisuje XML.
const EvidenceReceiver = ({
  receiver,
  onOpenPdf,
}: {
  receiver: Receiver;
  onOpenPdf: (evidenceId: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const name =
    receiver?.CompanyName ??
    `${receiver?.Name ?? ""} ${receiver?.Surname ?? ""}`.trim();

  return (
    <div className={styles.evidenceReceiver}>
      <div
        className={styles.evidenceReceiverHead}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{name || receiver.EDeliveryAddress}</span>
        <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
      </div>
      {open &&
        receiver?.Evidences?.map((e: Evidence) => (
          <div key={e.Id} className={styles.evidenceRow}>
            <div className={styles.evidenceInfo}>
              <div className={styles.evidenceName}>{e.Label}</div>
              <div className={styles.evidenceDate}>
                {fmtDateTime(e.CreationDate)}
              </div>
            </div>
            <div className={styles.evidenceButtons}>
              {(e.Event === EEvidenceEvent.SubmissionAcceptance ||
                e.Event === EEvidenceEvent.ContentHandover) && (
                <div
                  className={styles.iconBtn}
                  title="Podgląd dowodu doręczenia (PDF)"
                  onClick={() => onOpenPdf(e.Id)}
                >
                  <PdfIcon />
                </div>
              )}
              <div
                className={styles.iconBtn}
                title="Pobierz XML"
                onClick={() =>
                  downloadBlob(
                    `${API}/message/evidencexml?id=${e.Id}`,
                    `${e.Id}.xml`,
                    "application/xml",
                  )
                }
              >
                <DownloadIcon />
              </div>
            </div>
          </div>
        ))}
    </div>
  );
};

const EDeliveryMetricSlide: FC<Props> = ({ pack, onClose }) => {
  const { t } = useTranslation();
  const { data: metricData } = messagesService.useGetMetric(pack.Id, pack.Kind);
  const { data: statusesData } = statusService.useGetStatuses();
  const { mutate: getEpok } = CourtPortalService.useGetEpok();

  const metric: IMetric | null = useMemo(
    () => (metricData?.data ?? null) as IMetric | null,
    [metricData],
  );
  const statusList = useMemo(() => statusesData?.data ?? [], [statusesData]);

  // Domyślnie rozwinięta tylko sekcja „Dane podstawowe", pozostałe zwinięte.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    data: false,
    receivers: true,
    cc: true,
    evidence: true,
    history: true,
    pass: true,
  });
  const toggle = (key: string) =>
    setCollapsed((p) => ({ ...p, [key]: !p[key] }));

  // Podgląd dowodu doręczenia (PDF) w panelu slide dostawianym do metryki.
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const handleOpenPdf = async (evidenceId: string) => {
    try {
      const blob = await fetchBlob(`${API}/message/evidence?id=${evidenceId}`);
      setPdfBlob(blob);
    } catch {
      /* brak pliku (np. dane demonstracyjne) — podgląd pomijany */
    }
  };

  // Rządowe e-Doręczenia i ePUAP: dowody trzymamy lokalnie (plaintext), pod odrębnym endpointem.
  // Zwrócony plik PDF pokazujemy w podglądzie, pozostałe (XML) pobieramy.
  const isGov = pack.Kind === ItemKind.GovEDeliveryMessage;
  const isEpuap = pack.Kind === ItemKind.EpuapMessage;
  const isExternal = isGov || isEpuap;
  const handleOpenGovEvidence = async (evidenceId: string) => {
    try {
      const token = getUser()?.access_token;
      const res = await axios.get(
        `${API}/${isEpuap ? "epuap" : "govEDelivery"}/evidence?id=${evidenceId}`,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const blob = res.data as Blob;
      const type = blob.type || (res.headers?.["content-type"] as string) || "";
      if (type.includes("pdf")) {
        setPdfBlob(new Blob([blob], { type: "application/pdf" }));
      } else {
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.setAttribute("download", `dowod_${evidenceId}.xml`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch {
      /* brak pliku (np. dane demonstracyjne) — podgląd pomijany */
    }
  };

  if (!metric) {
    return createPortal(
      <div
        className={styles.overlay}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className={styles.panel}>
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <span className={styles.headerTitle}>{t("metric.title")}</span>
            </div>
            <button className={styles.closeBtn} onClick={onClose} title="Zamknij">
              ✕
            </button>
          </div>
          <div className={styles.body}>
            <div className={styles.noItems}>Ładowanie metryki…</div>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  const senderName =
    metric.Sender?.CompanyName ??
    `${metric.Sender?.Name ?? ""} ${metric.Sender?.Surname ?? ""}`.trim();

  const docNumber = pack.S4 ?? pack.Id;

  const receivedByName =
    metric.Receivers?.length && metric.ReceiveDate
      ? metric.Receivers[0].CompanyName ??
        `${metric.Receivers[0].Name ?? ""} ${
          metric.Receivers[0].Surname ?? ""
        }`.trim()
      : "";

  const hasEvidences = isExternal
    ? !!metric.Evidences?.length
    : !!metric.Receivers?.map((r) => r.Evidences ?? []).flat().length;
  const hasCc = pack.Receivers?.find((r) => r.Cc || r.Bcc);
  const ccReceivers = pack.Receivers?.filter((r) => r.Cc || r.Bcc) ?? [];

  const SectionHeader = ({ id, label }: { id: string; label: string }) => (
    <div className={styles.sectionTitle} onClick={() => toggle(id)}>
      <span>{label}</span>
      <FontAwesomeIcon
        className={styles.sectionChevron}
        icon={collapsed[id] ? faChevronRight : faChevronDown}
      />
    </div>
  );

  const handleDownloadAll = () =>
    downloadBlob(`${API}/message/evidences?id=${pack.Id}`, `M-${pack.Id}.zip`);

  const handleDownloadEpok = () =>
    getEpok(
      { id: pack.Id },
      {
        onSuccess(data) {
          if (data.data?.Url) {
            const w = window.open(data.data.Url, "_blank", "noopener,noreferrer");
            if (w) w.opener = null;
          }
        },
      },
    );

  return createPortal(
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {pdfBlob && (
        <div className={styles.pdfPreview}>
          <PDFViewer
            file={pdfBlob}
            title="Dowód doręczenia"
            showCloseButton
            onCloseButton={() => setPdfBlob(null)}
          />
        </div>
      )}
      <div className={styles.panel}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerTitle}>{t("metric.title")}</span>
            {senderName && (
              <span className={styles.headerSub}>{senderName}</span>
            )}
          </div>
          <button className={styles.closeBtn} onClick={onClose} title="Zamknij">
            ✕
          </button>
        </div>

        <div className={styles.body}>
          {/* ── Dane podstawowe ── */}
          <SectionHeader id="data" label="Dane podstawowe" />
          {!collapsed.data && (
            <div className={styles.metaCard}>
              {metric.Subject && (
                <div className={styles.metaTitleRow}>
                  <div className={styles.metaTitleLabel}>{t("forms.title")}</div>
                  <div className={styles.metaTitleValue}>{metric.Subject}</div>
                </div>
              )}
              <div className={styles.metaGrid}>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>{t("common.type")}</span>
                  <span className={styles.metaValue}>
                    {pack.Receivers?.length
                      ? getParcelTypeName(pack.Receivers[0].ParcelType)
                      : getItemKindName(pack.Kind)}
                  </span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>
                    {t("metric.parcelNumber")}
                  </span>
                  <span className={styles.metaValue}>{docNumber}</span>
                </div>
                <div className={styles.metaItem} style={{ gridColumn: "span 2" }}>
                  <span className={styles.metaLabel}>{t("forms.sender")}</span>
                  <span className={styles.metaValue}>{senderName || "—"}</span>
                </div>
                {metric.Sender?.EDeliveryAddress && (
                  <div
                    className={styles.metaItem}
                    style={{ gridColumn: "span 2" }}
                  >
                    <span className={styles.metaLabel}>
                      {t("metric.senderADE")}
                    </span>
                    <span className={styles.metaValue}>
                      {metric.Sender.EDeliveryAddress}
                    </span>
                  </div>
                )}
                {receivedByName && (
                  <div
                    className={styles.metaItem}
                    style={{ gridColumn: "span 2" }}
                  >
                    <span className={styles.metaLabel}>
                      {t("packages.receivedBy")}
                    </span>
                    <span className={styles.metaValue}>{receivedByName}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Odbiorcy ── */}
          <SectionHeader id="receivers" label={t("metric.recipientsTo")} />
          {!collapsed.receivers &&
            (metric.Receivers?.length ? (
              <div className={styles.receivers}>
                {metric.Receivers.map((r, index) => (
                  <div key={index}>
                    <OutgoingParcelReceiverRow
                      pack={pack}
                      receiver={{
                        ...r,
                        ParcelType: EParcelType.EDelivery,
                        SendStatus: ESendStatus.SENT,
                        Bcc: false,
                        Cc: false,
                        NewestEvidenceEvent: metric.NewestEvidenceEvent,
                      }}
                      statuses={statusList}
                    />
                    {metric.Receivers.length - 1 > index && (
                      <div className={styles.divider} />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.noItems}>Brak odbiorców</div>
            ))}

          {/* ── Do wiadomości (DW) ── */}
          {hasCc && (
            <>
              <SectionHeader id="cc" label={t("metric.recipientsCc")} />
              {!collapsed.cc && (
                <div className={styles.receivers}>
                  {ccReceivers.map((r, index) => (
                    <div key={index}>
                      <OutgoingParcelReceiverRow
                        pack={pack}
                        receiver={r}
                        statuses={statusList}
                      />
                      {ccReceivers.length - 1 > index && (
                        <div className={styles.divider} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── Dowody ── */}
          {hasEvidences && (
            <>
              <SectionHeader id="evidence" label="Dowody doręczenia" />
              {!collapsed.evidence &&
                (isExternal ? (
                  // Rządowe e-Doręczenia / ePUAP: dowody są płaską listą przy przesyłce (nie per-odbiorca).
                  <div className={styles.evidenceList}>
                    {metric.Evidences?.map((e) => (
                      <div key={e.Id} className={styles.evidenceRow}>
                        <div className={styles.evidenceInfo}>
                          <div className={styles.evidenceName}>{e.Label}</div>
                          <div className={styles.evidenceDate}>
                            {fmtDateTime(e.CreationDate)}
                          </div>
                        </div>
                        {e.HasFile && (
                          <div className={styles.evidenceButtons}>
                            <div
                              className={styles.iconBtn}
                              title="Podgląd / pobierz dowód"
                              onClick={() => handleOpenGovEvidence(e.Id)}
                            >
                              <PdfIcon />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className={styles.evidenceList}>
                      {metric.Receivers?.map((r, i) =>
                        r.Evidences ? (
                          <EvidenceReceiver
                            key={i}
                            receiver={r}
                            onOpenPdf={handleOpenPdf}
                          />
                        ) : null,
                      )}
                    </div>
                    <div
                      className={styles.downloadLink}
                      onClick={handleDownloadAll}
                    >
                      {t("metric.downloadAll")}
                    </div>
                  </>
                ))}
            </>
          )}

          {/* ── Historia ── */}
          {metric.History && metric.History.length > 0 && (
            <>
              <SectionHeader id="history" label="Etapy doręczenia" />
              {!collapsed.history && (
                <div className={styles.historyList}>
                  {metric.History.map((h) => (
                    <div
                      key={`history_${h.Id ?? randomString()}`}
                      className={styles.historyItem}
                    >
                      <Avatar
                        name={
                          h.User ||
                          (pack.Kind === ItemKind.CourtPortalDeliverance
                            ? "Portal Informacyjny"
                            : "—")
                        }
                        size="small"
                        className={styles.historyAvatar}
                      />
                      <div className={styles.historyContent}>
                        <div className={styles.historyDesc}>{h.Description}</div>
                        {h.User && (
                          <div className={styles.historyUser}>
                            {t("packages.receivedBy")}: {h.User}
                          </div>
                        )}
                        <div className={styles.historyDate}>
                          {fmtDateTime(h.Date)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── EPOK ── */}
          {metric.HasEpok && (
            <div className={styles.downloadLink} onClick={handleDownloadEpok}>
              {t("metric.downloadEpok")}
            </div>
          )}

          {/* ── Historia przekazań ── */}
          {metric.PassHistory && metric.PassHistory.length > 0 && (
            <>
              <SectionHeader id="pass" label="Przekazywanie wewnętrzne" />
              {!collapsed.pass && (
                <div className={styles.historyList}>
                  {metric.PassHistory.map((h) => (
                    <div key={h.Id} className={styles.historyItem}>
                      <Avatar
                        name={h.From}
                        size="small"
                        className={styles.historyAvatar}
                      />
                      <div className={styles.historyContent}>
                        <div className={styles.historyUser}>{h.From}</div>
                        <div className={styles.historyDesc}>
                          {t("metric.transferTo")} {h.To}
                        </div>
                        <div className={styles.historyDate}>
                          {fmtDateTime(h.Date)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EDeliveryMetricSlide;
